import { useState, useCallback } from 'react';
import {
  CustomerOrder,
  CustomerOrderResponse,
  SearchCustomerOrdersRequest,
  ACLAuthorizationRequest,
  SearchCustomerOrdersPostContentTypeEnum,
  CreateCustomerOrderContentTypeEnum,
} from '@/api/bol';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getCustomerOrdersApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseCustomerOrdersReturn {
  customerOrders: CustomerOrderResponse[];
  loading: boolean;
  searchError: string | null;
  selectedCustomerOrder: CustomerOrderResponse | null;
  setSelectedCustomerOrder: (customerOrder: CustomerOrderResponse | null) => void;
  fetchCustomerOrders: (page?: number, pageSize?: number, searchBody?: SearchCustomerOrdersRequest) => Promise<boolean>;
  createCustomerOrder: (customerOrderData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteCustomerOrder: (customerOrderId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useCustomerOrders = (): UseCustomerOrdersReturn => {
  const [items, setItems] = useState<CustomerOrderResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedCustomerOrder, setSelectedCustomerOrder] = useState<CustomerOrderResponse | null>(null);
  const [total, setTotal] = useState(0);

  const { showSuccess, showError } = useSnackbarNotification();

  const fetchCustomerOrders = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchCustomerOrdersRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);
    setLoading(true);
    setSearchError(null);

    try {
      const api = getCustomerOrdersApi();
      const response = await api.searchCustomerOrdersPost({
        contentType: SearchCustomerOrdersPostContentTypeEnum.ApplicationJson,
        searchCustomerOrdersRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize,
      });

      setItems(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch customer-orders');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createCustomerOrder = useCallback(async (customerOrderData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      const api = getCustomerOrdersApi();

      const createdCustomerOrder = await api.createCustomerOrder({
        contentType: CreateCustomerOrderContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        customerOrder: customerOrderData as CustomerOrder,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined,
      });

      const createdCustomerOrderId = createdCustomerOrder?.id;

      showSuccess(
        'CustomerOrder created successfully',
        createdCustomerOrderId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('customer-order', createdCustomerOrderId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Create customer-order', 'Failed to create customer-order');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  const deleteCustomerOrder = useCallback(async (customerOrderId: string) => {
    try {
      const api = getCustomerOrdersApi();
      await api.deleteCustomerOrderById({ customerOrderId });
      showSuccess('CustomerOrder deleted successfully');
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Delete customer-order', 'Failed to delete customer-order');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  return {
    customerOrders: items,
    loading,
    searchError,
    selectedCustomerOrder,
    setSelectedCustomerOrder,
    fetchCustomerOrders,
    createCustomerOrder,
    deleteCustomerOrder,
    setTotal,
    total,
  };
};
